import { NgModule } from '@angular/core';
import { Routes, RouterModule, Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { IRemoteXPACPortalState } from './utils/interfaces/store.interfaces';
import { selectUserData } from './store/selectors/auth.selectors';
import { hasValue } from './angular-core/utils/functions/common.functions';
import { filter, take } from 'rxjs';
import { GoogleAnalyticsService } from './services/google-analytics.service';
import { HeaderService } from './angular-core/services/header.service';

export enum RouterPath {
  Licenses = 'licenses',
  Service = 'service',
  More = 'more',
  Articles = 'articles',
  Preferences = 'preferences',
  ArgusCalculator = 'argus-calculator',
  Logistics = 'logistics',
  ProductionTooling = 'production-tooling',
  Products = 'products',
  Firmware = 'firmware',
  Documents = 'documents',
  Insights = 'insights',
  XPACManagement = 'xpac-management',
  Dashboard = 'dashboard',
  Impersonation = 'impersonation',
  Password = 'password',
  Unlock = 'unlock',
  Offline = 'offline',
  Companies = 'companies',
  Users = 'users',
  InventoryManagement = 'inventory-management',
}

const DEFAULT_ROUTE: RouterPath = RouterPath.Dashboard;
export const defaultRoutes: Routes = [
  {
    path: RouterPath.Dashboard,
    loadChildren: () => import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
    data: { breadcrumb: 'Dashboard', animation: RouterPath.Dashboard }
  },
  {
    path: RouterPath.Impersonation,
    loadChildren: () => import('./modules/impersonation/impersonation.module').then((m) => m.ImpersonationModule),
    data: { breadcrumb: 'Impersonation', animation: RouterPath.Impersonation, showbackbutton: true }
  },
  {
    path: RouterPath.Offline,
    loadChildren: () => import('./modules/offline/offline.module').then((m) => m.OfflineModule),
    data: { breadcrumb: 'Offline' }
  },
  {
    path: RouterPath.Articles,
    loadChildren: () => import('./modules/news/news.module').then(m => m.NewsModule),
    data: { breadcrumb: 'News', animation: RouterPath.Articles }
  },
  {
    path: RouterPath.Licenses,
    loadChildren: () => import('./modules/licenses/licenses.module').then(m => m.LicensesModule),
    data: { breadcrumb: 'Licenses', animation: RouterPath.Licenses, showbackbutton: true }
  },
  {
    path: RouterPath.Service,
    loadChildren: () => import('./modules/service/service.module').then(m => m.ServiceModule),
    data: { breadcrumb: 'Service', animation: RouterPath.Service }
  },
  {
    path: RouterPath.More,
    loadChildren: () => import('./modules/more/more.module').then(m => m.MoreModule),
    data: { breadcrumb: 'More', animation: RouterPath.More }
  },
  {
    path: RouterPath.Preferences,
    loadChildren: () => import('./modules/more/preferences/preferences.module').then((m) => m.PreferencesModule),
    data: { breadcrumb: 'Preferences', animation: RouterPath.Preferences }
  },
  {
    path: RouterPath.ArgusCalculator,
    loadChildren: () => import('./modules/more/argus-calculator/argus-calculator.module').then((m) => m.ArgusCalculatorModule),
    data: { breadcrumb: 'Argus', animation: RouterPath.ArgusCalculator }
  },
  {
    path: RouterPath.Logistics,
    loadChildren: () => import('./modules/more/logistics/logistics.module').then((m) => m.LogisticsModule),
    data: { breadcrumb: 'Logistics', animation: RouterPath.Logistics, showbackbutton: true }
  },
  {
    path: RouterPath.ProductionTooling,
    loadChildren: () => import('./modules/production-tooling/production-tooling.module').then((m) => m.ProductionToolingModule),
    data: { breadcrumb: 'ProductionTooling', animation: RouterPath.ProductionTooling, showbackbutton: true }
  },
  {
    path: RouterPath.Firmware,
    loadChildren: () => import('./modules/firmware/firmware.module').then((m) => m.FirmwareModule),
    data: { breadcrumb: 'Firmware', animation: RouterPath.Firmware, showbackbutton: true }
  },
  {
    path: RouterPath.Documents,
    loadChildren: () => import('./modules/documents/documents.module').then((m) => m.DocumentsModule),
    data: { breadcrumb: 'Documents', animation: RouterPath.Documents, showbackbutton: true }
  },
  {
    path: RouterPath.Insights,
    loadChildren: () => import('./modules/insights/insights.module').then((m) => m.InsightsModule),
    data: { breadcrumb: 'Insights', animation: RouterPath.Insights, showbackbutton: true }
  },
  {
    path: RouterPath.Password,
    loadChildren: () => import('./modules/device-password/device-password.module').then((m) => m.DevicePasswordModule),
    data: { breadcrumb: 'Password', animation: RouterPath.Password, showbackbutton: true }
  },
  {
    path: RouterPath.Unlock,
    loadChildren: () => import('./modules/device-unlock/device-unlock.module').then((m) => m.DeviceUnlockModule),
    data: { breadcrumb: 'Unlock', animation: RouterPath.Unlock, showbackbutton: true }
  },
  {
    path: RouterPath.Products,
    loadChildren: () => import('./modules/more/price-list/price-list.module').then((m) => m.PriceListModule),
    data: { breadcrumb: 'PriceList', animation: RouterPath.Products, showbackbutton: true }
  },
  {
    path: RouterPath.XPACManagement,
    loadChildren: () => import('./modules/more/control-monitor/xpac-management.module').then((m) => m.XPACManagementModule),
    data: { breadcrumb: 'Management', animation: RouterPath.XPACManagement, showbackbutton: true }
  },
  {
    path: RouterPath.Companies,
    loadChildren: () => import('./modules/companies/companies.module').then((m) => m.CompaniesModule),
    data: { breadcrumb: 'Companies', animation: RouterPath.Companies, showbackbutton: true }
  },
  {
    path: RouterPath.Users,
    loadChildren: () => import('./modules/users/users.module').then((m) => m.UsersModule),
    data: { breadcrumb: 'Users', animation: RouterPath.Users, showbackbutton: true }
  },
  {
    path: RouterPath.InventoryManagement,
    loadChildren: () => import('./modules/inventory-management/inventory-management.module').then((m) => m.InventoryManagementModule),
    data: { breadcrumb: 'InventoryManagement', animation: RouterPath.InventoryManagement, showbackbutton: true }
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: DEFAULT_ROUTE
  },
  {
    path: '**',
    redirectTo: DEFAULT_ROUTE
  },
];

declare global {
  function gtag(...args: any[]): void;
}

@NgModule({
  imports: [RouterModule.forRoot(defaultRoutes, {})],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor(
    private readonly router: Router,
    private readonly store: Store<IRemoteXPACPortalState>,
    private readonly googleAnalytics: GoogleAnalyticsService,
    private readonly headerService: HeaderService,
  ) {

    // Google Analytics 4 integration    
    this.store.select(selectUserData)
      .pipe(
        filter(hasValue),
      )
      .subscribe((user) => {
        // Enable pageview tracking for logged-in users
        if (user.userId) {
          this.googleAnalytics.initialize();
        }
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.headerService.onTitleChangedCallback.pipe(take(1)).subscribe((header) => {
          this.googleAnalytics.trackPageView(header?.title ?? document.title, document.location.origin + event.urlAfterRedirects);
        });
      }
    });
  }
}
